// Footer.tsx
import { Link } from "react-router-dom";
import { FaFacebook, FaInstagram, FaYoutube } from "react-icons/fa";
import { useLang } from "@/i18n/LanguageContext";

const socialLinks = [
  {
    href: "https://www.facebook.com/sofycaressma/?locale=es_LA",
    icon: FaFacebook,
    label: "Facebook",
    target: "_blank",
  },
  {
    href: "https://www.instagram.com/sofycaressma/",
    icon: FaInstagram,
    label: "Instagram",
    target: "_blank",
  },
  {
    href: "https://www.youtube.com/@SofyCaresSMA",
    icon: FaYoutube,
    label: "YouTube",
    target: "_blank",
  },
];

const legalLinks = [
  { href: "#privacy", key: "privacy" as const },
  { href: "#terms", key: "terms" as const },
  { href: "#accessibility", key: "accessibility" as const },
];

export default function Footer() {
  const { t } = useLang();

  const quickLinks = [
    { href: "/", text: t.footer.quickLinks.home },
    { href: "/about", text: t.footer.quickLinks.aboutUs },
    { href: "/services", text: t.footer.quickLinks.services },
    { href: "/contact", text: t.footer.quickLinks.contact },
  ];

  const servicesList = [
    { href: "/independent-living", text: t.footer.services.independentLiving },
    { href: "/assisted-living", text: t.footer.services.assistedLiving },
    {
      href: "/services#health-medical-services",
      text: t.footer.services.healthMedical,
    },
    { href: "/services#levels-of-care", text: t.footer.services.levelsOfCare },
    {
      href: "/services#services-amenities",
      text: t.footer.services.servicesAmenities,
    },
  ];

  return (
    <footer className="bg-primary-900 text-white">
      {/* Main footer grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* 1. Company Info */}
          <div>
            <div className="mb-6">
              <img
                src="/logo/logo-tll.svg"
                alt={t.footer.logoAlt}
                className="h-12"
              />
            </div>

            <p className="text-white/80 mb-6 leading-relaxed">
              {t.footer.tagline}
            </p>

            <div className="flex space-x-4">
              {socialLinks.map((s, i) => {
                const Icon = s.icon;
                return (
                  <a
                    key={i}
                    href={s.href}
                    target={s.target}
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors duration-200"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* 2. Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">
              {t.footer.quickLinksTitle}
            </h4>
            <ul className="space-y-4">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  {link.href.startsWith("#") ? (
                    <a
                      href={link.href}
                      className="text-white/80 hover:text-white transition-colors duration-200"
                    >
                      {link.text}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-white/80 hover:text-white transition-colors duration-200"
                    >
                      {link.text}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* 3. Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6">
              {t.footer.servicesTitle}
            </h4>
            <ul className="space-y-4">
              {servicesList.map((service, i) => (
                <li key={i}>
                  <Link
                    to={service.href}
                    className="text-white/80 hover:text-white transition-colors duration-200"
                  >
                    {service.text}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 4. Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-6">
              {t.footer.contactTitle}
            </h4>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <svg
                  className="w-5 h-5 text-accent-300 mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>

                <p className="text-white/80">
                  {t.footer.addressLine1}
                  <br />
                  {t.footer.addressLine2}
                </p>
              </div>

              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-accent-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>

                <a
                  href="tel:+524151177643"
                  className="text-white/80 hover:text-white transition-colors duration-200"
                >
                  +52 415 117 7643
                </a>
              </div>

              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-accent-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>

                <a
                  href="mailto:sofycaressma@gmail.com"
                  className="text-white/80 hover:text-white transition-colors duration-200"
                >
                  sofycaressma@gmail.com
                </a>
              </div>

              <div className="mt-6">
                <p className="text-accent-300 font-medium mb-2">
                  {t.footer.available247Title}
                </p>
                <p className="text-white/80 text-sm">
                  {t.footer.available247Sub}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 bg-text-primary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-white/60 text-sm">{t.footer.copyright}</p>

            <div className="flex space-x-6 text-sm">
              {legalLinks.map((l, i) => (
                <a
                  key={i}
                  href={l.href}
                  className="text-white/60 hover:text-white transition-colors duration-200"
                >
                  {t.footer.legal[l.key]}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
